import { createContext, ReactNode, useEffect, useState } from "react";
import { useIdentityVerificationFilteredPairs } from "../hooks/identityVerification/useIdentityVerificationFilteredPairs";
import { useDocumentationArea } from "../hooks/useDocumentationArea";
import { useDiscardIdentityVerificationStagedFile } from "../hooks/useDiscardIdentityVerificationStagedFile";
import { useSelectIdentityVerificationCountry } from "../hooks/identityVerification/useSelectIdentityVerificationCountry";
import { useSelectIdentityVerificationDocumentType } from "../hooks/identityVerification/useSelectIdentityVerificationDocumentType";
import { IdentityVerificationDocumentTypes } from "src/contexts/identityVerification/domain/IdentityVerificationDocumentTypes";
import { AllowedFrontEndPair } from "src/contexts/identityVerification/application/AllowedFrontendPair";
import { OrderStatus } from "src/contexts/documentationArea/domain/OrderStatus";

export type IdentityVerificationContextType = {
  allowedPairs: AllowedFrontEndPair[];
  selectedCountry: string | null;
  selectCountry: (country: string) => void;
  selectedDocumentType: IdentityVerificationDocumentTypes | null;
  selectDocumentType: (documentType: IdentityVerificationDocumentTypes) => void;
  discardFile: (fileId: string) => void;
  discardFileLoading: boolean;
  identityVerificationStatus: OrderStatus;
};

export const IdentityVerificationContext = createContext<IdentityVerificationContextType>(
  {} as IdentityVerificationContextType,
);

/**
 * This component provides the context for identity verification.
 * It manages the selected country and document type, and provides
 * functions to select them and discard files in a unified way for
 * the different components that handle this logic.
 */

export function IdentityVerificationProvider({ children }: { children: ReactNode }) {
  const { identityVerifications } = useDocumentationArea();

  const apiSelectedDocumentType =
    identityVerifications[0]?.getRequest().data.stagedState.documentType;
  const apiSelectedCountry = identityVerifications[0]?.getRequest().data.stagedState.country;
  const { filteredAllowedPairs } = useIdentityVerificationFilteredPairs();
  const { mutate: discard, isPending: discardFileLoading } =
    useDiscardIdentityVerificationStagedFile();
  const { mutate: selectCountryAPI } = useSelectIdentityVerificationCountry();
  const { mutate: selectDocumentTypeAPI } = useSelectIdentityVerificationDocumentType();
  const [selectedDocumentType, setSelectedDocumentType] =
    useState<IdentityVerificationDocumentTypes | null>(apiSelectedDocumentType);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(apiSelectedCountry ?? null);

  const identityVerificationStatus = identityVerifications[0]?.getStatus();

  const discardFile = (fileId: string) => {
    const identityVerificationId = identityVerifications
      .find(
        (idv) =>
          idv.getRequest().data.stagedState.files.front?.id === fileId ||
          idv.getRequest().data.stagedState.files.back?.id === fileId,
      )
      ?.getId();
    if (!identityVerificationId) throw Error("idvId not found for the given fileId");
    discard({ identityVerificationId, fileId });
  };

  const selectCountry = (country: string) => {
    selectCountryAPI({ identityVerificationId: identityVerifications[0].getId(), country });
    setSelectedCountry(country);
  };

  const selectDocumentType = (documentType: IdentityVerificationDocumentTypes) => {
    selectDocumentTypeAPI({
      identityVerificationId: identityVerifications[0].getId(),
      documentType,
    });
    setSelectedCountry(null);
    setSelectedDocumentType(documentType);
  };

  useEffect(() => {
    const apiCountry = identityVerifications[0]?.getRequest().data.stagedState.country;
    const apiDocumentType = identityVerifications[0]?.getRequest().data.stagedState.documentType;
    if (apiCountry) {
      setSelectedCountry(apiCountry);
    }
    if (apiDocumentType) {
      setSelectedDocumentType(apiDocumentType);
    }
    // Patch to make invalidate endpoint work
    if (apiDocumentType === null && selectedDocumentType !== null) {
      setSelectedDocumentType(null);
    }
    if (apiCountry === null && selectedCountry !== null) {
      setSelectedCountry(null);
    }
  }, [
    identityVerifications[0].getRequest().data.stagedState.country,
    identityVerifications[0].getRequest().data.stagedState.documentType,
  ]);

  return (
    <IdentityVerificationContext.Provider
      value={{
        allowedPairs: filteredAllowedPairs,
        selectedCountry,
        selectedDocumentType,
        selectCountry,
        selectDocumentType,
        discardFile,
        discardFileLoading,
        identityVerificationStatus,
      }}
    >
      {children}
    </IdentityVerificationContext.Provider>
  );
}
